import type {
  CreateStoryPreludeCompositionInput,
  CreateSeedanceVideoGenerationInput,
  SeedanceProviderConfigView,
  SeedanceVideoGenerationStatus,
  SeedanceVideoGenerationView,
  StoryListItem,
  StoryPreludeCompositionAudioMode,
  StoryPreludeCompositionSource,
  StoryPreludeCompositionSourceKind,
  StoryPreludeCompositionStatus,
  StoryPreludeCompositionView,
  UploadedStoryVideoClipView,
} from "@actale/contracts";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { api, ApiError, storyEntryMediaUrl } from "./api.js";
import { formatUpdatedAt } from "./story-utils.js";

interface GenerationForm {
  storyId: string;
  prompt: string;
  referenceImageUrls: string;
  referenceVideoUrls: string;
  referenceAudioUrls: string;
  ratio: CreateSeedanceVideoGenerationInput["ratio"];
  resolution: CreateSeedanceVideoGenerationInput["resolution"];
  duration: number;
  generateAudio: boolean;
  costConfirmed: boolean;
}

interface LighthouseStarterFields {
  storyId: string;
  prompt: string;
  referenceImageUrls: string;
}

export interface CompositionCandidate {
  key: string;
  kind: StoryPreludeCompositionSourceKind;
  id: string;
  label: string;
  sourceLabel: string;
  duration: number;
  managedVideoKey: string;
  managedVideoHasAudio: boolean | null;
  requestedAudio?: boolean;
  createdAt: string;
}

const LIGHTHOUSE_STORY_SLUG = "mist-over-lighthouse";
const MAX_UPLOADED_CLIP_BYTES = 60 * 1024 * 1024;

export const LIGHTHOUSE_REFERENCE_IMAGE_URLS = [
  "https://admin.actale.cc/story-entry-videos/mist-entry-boat-safe-v2.png",
];

export const LIGHTHOUSE_CHEN_ASSET_URI = "asset://asset-20260720211421-k5pgp";

export const LIGHTHOUSE_VIDEO_PROMPT = `全程 9:16 竖屏，8 秒，电影写实悬疑风格。以图片1为唯一人物、船只、灯塔和空码头参考；保持三个人的背影、服装颜色、无线电和设备箱一致。

0—5 秒：镜头从三人背后跟随小船在暴风雨中驶向雾中灯塔。中间的人举起无线电，画外音低声说：“林叔，收到请回答。”随后只有短促的无线电静电声，没有回应。

5—8 秒：小船逼近码头，灯塔光束扫过海面，码头空无一人。镜头停在空码头、灯塔和船上三人的背影。

三个人始终戴着帽子背对镜头，不出现正脸、侧脸、皮肤或可识别肖像。不要字幕、文字、标题、Logo、水印、新增人物、岸边接船人、怪物、武器、城市、直升机或超自然特效；不要快速闪切。声音只使用画外音、风声、雨声、海浪、船发动机和无线电静电声。`;

export function applyLighthouseStarter<T extends LighthouseStarterFields>(
  current: T,
  storyId: string,
  overwrite = false,
): T {
  return {
    ...current,
    storyId,
    prompt: overwrite || !current.prompt.trim() ? LIGHTHOUSE_VIDEO_PROMPT : current.prompt,
    referenceImageUrls: overwrite || !current.referenceImageUrls.trim()
      ? LIGHTHOUSE_REFERENCE_IMAGE_URLS.join("\n")
      : current.referenceImageUrls,
  };
}

export function applyLighthouseChenAsset<T extends LighthouseStarterFields>(current: T, storyId: string): T {
  return {
    ...current,
    storyId,
    referenceImageUrls: LIGHTHOUSE_CHEN_ASSET_URI,
  };
}

const initialForm: GenerationForm = {
  storyId: "",
  prompt: "",
  referenceImageUrls: "",
  referenceVideoUrls: "",
  referenceAudioUrls: "",
  ratio: "9:16",
  resolution: "720p",
  duration: 8,
  generateAudio: true,
  costConfirmed: false,
};

const terminalStatuses = new Set<SeedanceVideoGenerationStatus>([
  "SUCCEEDED",
  "FAILED",
  "EXPIRED",
  "CANCELLED",
]);

const statusText: Record<SeedanceVideoGenerationStatus, string> = {
  SUBMITTING: "正在提交",
  QUEUED: "排队中",
  RUNNING: "生成中",
  SUCCEEDED: "已完成",
  FAILED: "失败",
  EXPIRED: "已超时",
  CANCELLED: "已取消",
};

const compositionStatusText: Record<StoryPreludeCompositionStatus, string> = {
  QUEUED: "等待合成",
  RUNNING: "正在合成",
  SUCCEEDED: "已完成",
  FAILED: "合成失败",
};

const audioStatusText: Record<StoryPreludeCompositionView["audioStatus"], string> = {
  NONE: "未上传母音轨",
  QUEUED: "等待合成有声版",
  RUNNING: "正在合成有声版",
  SUCCEEDED: "有声版已完成",
  FAILED: "有声版合成失败",
};

export const compositionAudioModeCopy: Record<StoryPreludeCompositionAudioMode, {
  title: string;
  description: string;
  outputLabel: string;
}> = {
  SILENT: {
    title: "无声画面版（默认）",
    description: "移除所有片段的原始音频，只保留连续画面。",
    outputLabel: "无声画面版",
  },
  PRESERVE_SOURCE_AUDIO: {
    title: "保留人物同步声音",
    description: "保留有声片段的原始同步音轨；无声环境片段由系统补静音后参与合成。",
    outputLabel: "原片同步声音版",
  },
};

export const DEFAULT_COMPOSITION_AUDIO_MODE: StoryPreludeCompositionAudioMode = "SILENT";

export function compositionCandidateKey(kind: StoryPreludeCompositionSourceKind, id: string): string {
  return `${kind}:${id}`;
}

export function buildStoryPreludeCompositionSources(
  candidates: CompositionCandidate[],
): StoryPreludeCompositionSource[] {
  return candidates.map((candidate) => ({ kind: candidate.kind, id: candidate.id }));
}

function formatClipDuration(seconds: number): string {
  return Number.isInteger(seconds) ? String(seconds) : seconds.toFixed(1);
}

function formatMegabytes(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

export function sourceClipAudioLabel(
  managedVideoHasAudio: boolean | null,
  requestedAudio?: boolean,
): string {
  if (managedVideoHasAudio === true) return "已检测：原片有声";
  if (managedVideoHasAudio === false) return "已检测：原片无声";
  if (requestedAudio === true) return "请求有声 · 尚未检测";
  if (requestedAudio === false) return "请求无声 · 尚未检测";
  return "音轨尚未检测";
}

export function compositionPreferredOutputLabel(
  audioMode: StoryPreludeCompositionAudioMode,
  audioStatus: StoryPreludeCompositionView["audioStatus"],
): string {
  if (audioStatus !== "SUCCEEDED") return compositionAudioModeCopy[audioMode].outputLabel;
  return audioMode === "PRESERVE_SOURCE_AUDIO"
    ? "对白与环境声混音版"
    : "母音轨替换版";
}

export function compositionAudioStatusLabel(
  audioMode: StoryPreludeCompositionAudioMode,
  audioStatus: StoryPreludeCompositionView["audioStatus"],
): string {
  if (audioMode === "SILENT") return audioStatusText[audioStatus];
  return {
    NONE: "未上传环境/音乐底轨",
    QUEUED: "等待混合环境/音乐底轨",
    RUNNING: "正在混合对白与底轨",
    SUCCEEDED: "对白与环境声混音版已完成",
    FAILED: "环境/音乐底轨混音失败",
  }[audioStatus];
}

export function parseReferenceUrls(value: string): string[] {
  return value.split(/\r?\n/u).map((item) => item.trim()).filter(Boolean);
}

function message(caught: unknown, fallback: string): string {
  return caught instanceof ApiError ? caught.message : fallback;
}

function replaceJob(jobs: SeedanceVideoGenerationView[], next: SeedanceVideoGenerationView) {
  const found = jobs.some((job) => job.id === next.id);
  return found ? jobs.map((job) => job.id === next.id ? next : job) : [next, ...jobs];
}

function replaceComposition(
  compositions: StoryPreludeCompositionView[],
  next: StoryPreludeCompositionView,
) {
  const found = compositions.some((composition) => composition.id === next.id);
  return found
    ? compositions.map((composition) => composition.id === next.id ? next : composition)
    : [next, ...compositions];
}

export function SeedanceVideoPage({ onConfigureProvider }: { onConfigureProvider?: () => void }): React.ReactNode {
  const [config, setConfig] = useState<SeedanceProviderConfigView | null>(null);
  const [stories, setStories] = useState<StoryListItem[]>([]);
  const [jobs, setJobs] = useState<SeedanceVideoGenerationView[]>([]);
  const [uploadedClips, setUploadedClips] = useState<UploadedStoryVideoClipView[]>([]);
  const [compositions, setCompositions] = useState<StoryPreludeCompositionView[]>([]);
  const [form, setForm] = useState<GenerationForm>(initialForm);
  const [uploadStoryId, setUploadStoryId] = useState("");
  const [uploadLabel, setUploadLabel] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadClientRequestId, setUploadClientRequestId] = useState(() => crypto.randomUUID());
  const [uploadInputKey, setUploadInputKey] = useState(0);
  const [compositionStoryId, setCompositionStoryId] = useState("");
  const [compositionSourceKeys, setCompositionSourceKeys] = useState<string[]>([]);
  const [compositionAudioMode, setCompositionAudioMode] = useState<StoryPreludeCompositionAudioMode>(DEFAULT_COMPOSITION_AUDIO_MODE);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [refreshingId, setRefreshingId] = useState("");
  const [importingId, setImportingId] = useState("");
  const [uploadingClip, setUploadingClip] = useState(false);
  const [composing, setComposing] = useState(false);
  const [uploadingAudioId, setUploadingAudioId] = useState("");
  const [applyingCompositionId, setApplyingCompositionId] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [uploadNotice, setUploadNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [nextConfig, nextJobs, nextUploadedClips, nextCompositions, storyResult] = await Promise.all([
        api.seedanceProviderConfig(),
        api.seedanceVideoGenerations(),
        api.uploadedStoryVideoClips(),
        api.storyPreludeCompositions(),
        api.storyLines({ pageSize: 100 }),
      ]);
      setConfig(nextConfig);
      setJobs(nextJobs);
      setUploadedClips(nextUploadedClips);
      setCompositions(nextCompositions);
      setStories(storyResult.data);
      setForm((current) => {
        if (current.storyId) return current;
        const lighthouse = storyResult.data.find((story) => story.slug === LIGHTHOUSE_STORY_SLUG);
        return lighthouse ? applyLighthouseStarter(current, lighthouse.id) : current;
      });
      const initialStoryId = storyResult.data.find((story) => story.slug === LIGHTHOUSE_STORY_SLUG)?.id || storyResult.data[0]?.id || "";
      setUploadStoryId((current) => current || initialStoryId);
      setCompositionStoryId((current) => current || initialStoryId);
    } catch (caught) {
      setError(message(caught, "无法读取视频生成工作台"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const activeJobs = useMemo(
    () => jobs.filter((job) => !terminalStatuses.has(job.status)),
    [jobs],
  );

  useEffect(() => {
    if (!activeJobs.length) return;
    const timer = window.setInterval(() => {
      void Promise.all(activeJobs.map(async (job) => {
        try {
          const next = await api.refreshSeedanceVideoGeneration(job.id);
          setJobs((current) => replaceJob(current, next));
        } catch {
          // The explicit refresh button surfaces provider query errors to the operator.
        }
      }));
    }, 6_000);
    return () => window.clearInterval(timer);
  }, [activeJobs]);

  const activeCompositions = useMemo(
    () => compositions.filter((composition) => (
      composition.status === "QUEUED"
      || composition.status === "RUNNING"
      || composition.audioStatus === "QUEUED"
      || composition.audioStatus === "RUNNING"
    )),
    [compositions],
  );

  useEffect(() => {
    if (!activeCompositions.length) return;
    const timer = window.setInterval(() => {
      void api.storyPreludeCompositions().then(setCompositions).catch(() => {
        // The normal refresh button exposes errors without interrupting the current review.
      });
    }, 5_000);
    return () => window.clearInterval(timer);
  }, [activeCompositions.length]);

  async function createGeneration(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!config?.configured || !form.costConfirmed || !form.prompt.trim()) return;
    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      const next = await api.createSeedanceVideoGeneration({
        clientRequestId: crypto.randomUUID(),
        storyId: form.storyId || null,
        prompt: form.prompt.trim(),
        referenceImageUrls: parseReferenceUrls(form.referenceImageUrls),
        referenceVideoUrls: parseReferenceUrls(form.referenceVideoUrls),
        referenceAudioUrls: parseReferenceUrls(form.referenceAudioUrls),
        ratio: form.ratio,
        resolution: form.resolution,
        duration: form.duration,
        generateAudio: form.generateAudio,
        watermark: false,
        costConfirmed: true,
      });
      setJobs((current) => replaceJob(current, next));
      setForm((current) => ({ ...current, costConfirmed: false }));
      setNotice("任务已提交到火山方舟。页面会自动查询进度；生成不会修改故事草稿或 StoryVersion。");
    } catch (caught) {
      setError(message(caught, "视频生成任务提交失败"));
      try {
        setJobs(await api.seedanceVideoGenerations());
      } catch {
        // Preserve the original actionable submission error.
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function refreshJob(id: string): Promise<void> {
    setRefreshingId(id);
    setError("");
    try {
      const next = await api.refreshSeedanceVideoGeneration(id);
      setJobs((current) => replaceJob(current, next));
    } catch (caught) {
      setError(message(caught, "查询视频生成进度失败"));
    } finally {
      setRefreshingId("");
    }
  }

  async function importJob(id: string): Promise<void> {
    setImportingId(id);
    setError("");
    setNotice("");
    try {
      const next = await api.importSeedanceVideoGeneration(id);
      setJobs((current) => replaceJob(current, next));
      setNotice("成片已保存到 ACTALE，并完成快速起播处理。现在可以稳定播放和应用到故事草稿。");
    } catch (caught) {
      setError(message(caught, "成片保存失败"));
    } finally {
      setImportingId("");
    }
  }

  async function uploadDialogueClip(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (uploadingClip) return;
    const label = uploadLabel.trim();
    if (!uploadStoryId || !label || !uploadFile) {
      setUploadError("请选择故事线、填写片段名称并选择 MP4 文件。");
      return;
    }
    if (!uploadFile.name.toLowerCase().endsWith(".mp4")) {
      setUploadError("只支持 MP4 视频文件。");
      return;
    }
    if (uploadFile.size < 1_024 || uploadFile.size > MAX_UPLOADED_CLIP_BYTES) {
      setUploadError("视频大小必须在 1KB 到 60MB 之间。");
      return;
    }
    setUploadingClip(true);
    setUploadError("");
    setUploadNotice("");
    try {
      const clip = await api.uploadStoryVideoClip({
        storyId: uploadStoryId,
        label,
        clientRequestId: uploadClientRequestId,
      }, uploadFile);
      setUploadedClips((current) => [clip, ...current.filter((item) => item.id !== clip.id)]);
      setUploadLabel("");
      setUploadFile(null);
      setUploadClientRequestId(crypto.randomUUID());
      setUploadInputKey((current) => current + 1);
      setUploadNotice(`“${clip.label}”已导入并完成媒体检测；没有调用模型，也没有修改故事草稿。`);
    } catch (caught) {
      setUploadError(message(caught, "同步对话片段导入失败"));
    } finally {
      setUploadingClip(false);
    }
  }

  const compositionCandidates = useMemo<CompositionCandidate[]>(() => {
    const seedanceCandidates = jobs.filter((job) => (
      job.story?.id === compositionStoryId
      && job.status === "SUCCEEDED"
      && Boolean(job.managedVideoKey && job.managedAt)
      && job.ratio === "9:16"
      && job.resolution === "720p"
    )).map((job): CompositionCandidate => ({
      key: compositionCandidateKey("SEEDANCE_GENERATION", job.id),
      kind: "SEEDANCE_GENERATION",
      id: job.id,
      label: "Seedance 生成片段",
      sourceLabel: "Seedance",
      duration: job.duration,
      managedVideoKey: job.managedVideoKey!,
      managedVideoHasAudio: job.managedVideoHasAudio,
      requestedAudio: job.generateAudio,
      createdAt: job.createdAt,
    }));
    const uploadedCandidates = uploadedClips.filter((clip) => clip.story.id === compositionStoryId)
      .map((clip): CompositionCandidate => ({
        key: compositionCandidateKey("UPLOADED_CLIP", clip.id),
        kind: "UPLOADED_CLIP",
        id: clip.id,
        label: clip.label,
        sourceLabel: "导入片段",
        duration: clip.managedVideoDurationSeconds,
        managedVideoKey: clip.managedVideoKey,
        managedVideoHasAudio: clip.managedVideoHasAudio,
        createdAt: clip.createdAt,
      }));
    return [...seedanceCandidates, ...uploadedCandidates]
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
  }, [compositionStoryId, jobs, uploadedClips]);

  const selectedCompositionClips = useMemo(
    () => compositionSourceKeys
      .map((key) => compositionCandidates.find((candidate) => candidate.key === key))
      .filter((candidate): candidate is CompositionCandidate => Boolean(candidate)),
    [compositionCandidates, compositionSourceKeys],
  );

  const selectedCompositionDuration = selectedCompositionClips.reduce((total, job) => total + job.duration, 0);
  const selectedCompositionVoicedCount = selectedCompositionClips.filter((job) => job.managedVideoHasAudio === true).length;
  const selectedCompositionSilentCount = selectedCompositionClips.filter((job) => job.managedVideoHasAudio === false).length;
  const selectedCompositionUncheckedCount = selectedCompositionClips.filter((job) => job.managedVideoHasAudio == null).length;

  function toggleCompositionClip(key: string): void {
    setCompositionSourceKeys((current) => {
      if (current.includes(key)) return current.filter((item) => item !== key);
      return current.length >= 6 ? current : [...current, key];
    });
  }

  function moveCompositionClip(index: number, direction: -1 | 1): void {
    setCompositionSourceKeys((current) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.length) return current;
      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex]!, next[index]!];
      return next;
    });
  }

  async function createComposition(): Promise<void> {
    if (!compositionStoryId || selectedCompositionClips.length < 2 || composing) return;
    const modeCopy = compositionAudioModeCopy[compositionAudioMode];
    const audioEffect = compositionAudioMode === "PRESERVE_SOURCE_AUDIO"
      ? `当前已检测：${selectedCompositionVoicedCount} 个原片有声、${selectedCompositionSilentCount} 个原片无声、${selectedCompositionUncheckedCount} 个尚未检测。系统保留实际有声片段的同步声音，并为实际无声片段补静音。请确认人物对白在各自原片中已经音画同步。`
      : "所有源片段的原始音频都会被移除。";
    if (!window.confirm(`把已保存的 ${selectedCompositionClips.length} 个片段按当前顺序合成为“${modeCopy.outputLabel}”故事序幕？\n\n${audioEffect}\n\n这不会调用 Seedance、不会产生模型费用、不会自动修改故事草稿或发布 StoryVersion。`)) return;
    setComposing(true);
    setError("");
    setNotice("");
    try {
      const next = await api.createStoryPreludeComposition({
        storyId: compositionStoryId,
        sources: buildStoryPreludeCompositionSources(selectedCompositionClips),
        audioMode: compositionAudioMode,
      } satisfies CreateStoryPreludeCompositionInput);
      setCompositions((current) => replaceComposition(current, next));
      setCompositionSourceKeys([]);
      setNotice(`“${modeCopy.outputLabel}”已加入合成队列。不会调用 Seedance，也不会自动应用草稿或发布 StoryVersion。`);
    } catch (caught) {
      setError(message(caught, "故事序幕合成任务创建失败"));
    } finally {
      setComposing(false);
    }
  }

  async function applyComposition(composition: StoryPreludeCompositionView): Promise<void> {
    const story = stories.find((item) => item.id === composition.story.id);
    const expectedOutputVideoKey = composition.preferredOutputVideoKey;
    if (
      !story
      || !expectedOutputVideoKey
      || composition.audioStatus === "QUEUED"
      || composition.audioStatus === "RUNNING"
    ) return;
    const versionLabel = compositionPreferredOutputLabel(composition.audioMode, composition.audioStatus);
    if (!window.confirm(`把已评审的“${versionLabel}”应用到《${story.title}》草稿 R${story.draftRevision}？\n\n评审文件：${expectedOutputVideoKey}\n\n系统只会应用这个具体文件；如果合成结果已经变化，本次操作会被拒绝。应用会生成新的草稿修订，但不会发布 StoryVersion。`)) return;
    setApplyingCompositionId(composition.id);
    setError("");
    setNotice("");
    try {
      const result = await api.applyStoryPreludeCompositionToStoryDraft(composition.id, {
        expectedStoryRevision: story.draftRevision,
        expectedOutputVideoKey,
      });
      setCompositions((current) => replaceComposition(current, result.composition));
      setStories((current) => current.map((item) => item.id === result.story.id
        ? { ...item, draftRevision: result.story.draftRevision }
        : item));
      setNotice(`已将评审文件 ${expectedOutputVideoKey} 应用到《${result.story.title}》草稿 R${result.story.draftRevision}；StoryVersion 尚未发布。`);
    } catch (caught) {
      setError(message(caught, "无法应用完整故事序幕"));
    } finally {
      setApplyingCompositionId("");
    }
  }

  async function uploadMasterAudio(composition: StoryPreludeCompositionView, file: File): Promise<void> {
    const replacing = composition.audioStatus === "SUCCEEDED";
    const preservesSourceAudio = composition.audioMode === "PRESERVE_SOURCE_AUDIO";
    const audioAction = preservesSourceAudio ? "环境/音乐底轨" : "完整替换母音轨";
    const audioEffect = preservesSourceAudio
      ? "底轨会与各片段的原始同步对白混合。请先在外部预调底轨音量，且底轨本身不要包含人物对白。"
      : "母音轨会替换整部序幕的声音，不保留源片段音频。";
    if (!window.confirm(`${replacing ? "替换" : "上传"}《${composition.story.title}》的${audioAction}？\n\n${audioEffect}\n音频必须与画面时长相差不超过 2 秒。系统会保留基础合成片；不会自动应用草稿或发布 StoryVersion。`)) return;
    setUploadingAudioId(composition.id);
    setError("");
    setNotice("");
    try {
      const next = await api.uploadStoryPreludeMasterAudio(composition.id, file);
      setCompositions((current) => replaceComposition(current, next));
      setNotice(`${audioAction}已上传，正在生成${preservesSourceAudio ? "对白与环境声混音版" : "母音轨替换版"}。基础合成片仍保留，故事草稿和 StoryVersion 均未修改。`);
    } catch (caught) {
      setError(message(caught, "母音轨上传失败"));
    } finally {
      setUploadingAudioId("");
    }
  }

  function selectStory(storyId: string): void {
    const story = stories.find((item) => item.id === storyId);
    setForm((current) => story?.slug === LIGHTHOUSE_STORY_SLUG
      ? applyLighthouseStarter(current, storyId)
      : { ...current, storyId });
  }

  function useLighthouseStarter(): void {
    const lighthouse = stories.find((story) => story.slug === LIGHTHOUSE_STORY_SLUG);
    setForm((current) => applyLighthouseStarter(current, lighthouse?.id ?? current.storyId, true));
  }

  function useLighthouseChenAsset(): void {
    const lighthouse = stories.find((story) => story.slug === LIGHTHOUSE_STORY_SLUG);
    setForm((current) => applyLighthouseChenAsset(current, lighthouse?.id ?? current.storyId));
  }

  if (loading && !config) {
    return <div className="director-loading"><span /><strong>正在读取视频生成工作台…</strong></div>;
  }
  if (!config) {
    return <div className="state-card"><div className="error-notice" role="alert"><span>!</span>{error || "视频生成工作台不可用"}</div><button className="secondary-button" onClick={() => void load()}>重新加载</button></div>;
  }

  return (
    <div className="seedance-page">
      <div className="page-heading provider-config-heading">
        <div>
          <p className="eyebrow">AI VIDEO STUDIO</p>
          <h1>视频生成</h1>
          <p>使用火山方舟 Seedance 2.5 生成故事短视频；先生成和评审，再决定是否用于草稿。</p>
        </div>
        <button className="secondary-button" disabled={loading || submitting || uploadingClip} onClick={() => void load()}>{loading ? "正在刷新…" : "刷新工作台"}</button>
      </div>

      {error ? <div className="error-notice" role="alert"><span>!</span>{error}</div> : null}
      {notice ? <div className="director-notice" role="status">✓ {notice}</div> : null}
      <form className="seedance-generator" onSubmit={(event) => void createGeneration(event)}>
        <header>
          <div><strong>新建视频任务</strong><small>任务提交后由火山方舟异步生成 · Seedance {config.configured ? "已就绪" : "未配置"}</small></div>
          <span>不会修改故事草稿</span>
        </header>
        <div className="seedance-generator-body">
          {!config.configured ? <div className="seedance-config-required" role="alert"><div><strong>Seedance 尚未配置</strong><span>请先在“AI 模型设置”中完成视频模型设置。</span></div><button type="button" className="secondary-button" onClick={onConfigureProvider}>前往 AI 模型设置</button></div> : null}
          <label>
            <span>关联故事线（只用于记录来源）</span>
            <select value={form.storyId} onChange={(event) => selectStory(event.target.value)}>
              <option value="">不关联故事线</option>
              {stories.map((story) => <option key={story.id} value={story.id}>{story.title} · 草稿 R{story.draftRevision}</option>)}
            </select>
          </label>
          <label>
            <span>视频提示词</span>
            <textarea rows={9} maxLength={10_000} value={form.prompt} onChange={(event) => setForm({ ...form, prompt: event.target.value })} placeholder="按时间段写清画面、镜头、人物动作、声音和最终定格。提示词中的图片1、视频1、音频1按下方各类素材顺序编号。" required />
            <small>{form.prompt.length.toLocaleString()} / 10,000 字符</small>
          </label>

          <div className="seedance-reference-heading">
            <div><strong>参考素材</strong><small>参考图片可使用火山官方 asset:// Asset URI 或无查询参数 HTTPS；视频和音频仍只允许 HTTPS。服务器不会上传本地文件。</small></div>
            <div className="seedance-reference-actions">
              <button type="button" className="secondary-button" onClick={useLighthouseChenAsset}>填入陈渡官方人像</button>
              <button type="button" className="secondary-button" onClick={useLighthouseStarter}>填入灯塔安全单图方案</button>
            </div>
          </div>
          <div className="seedance-reference-grid">
            <label><span>参考图片（最多 9 个）</span><textarea rows={5} value={form.referenceImageUrls} onChange={(event) => setForm({ ...form, referenceImageUrls: event.target.value })} placeholder="asset://asset-...&#10;https://.../image-2.jpg" /></label>
            <label><span>参考视频（最多 3 个）</span><textarea rows={5} value={form.referenceVideoUrls} onChange={(event) => setForm({ ...form, referenceVideoUrls: event.target.value })} placeholder="https://.../reference.mp4" /></label>
            <label><span>参考音频（最多 3 个）</span><textarea rows={5} value={form.referenceAudioUrls} onChange={(event) => setForm({ ...form, referenceAudioUrls: event.target.value })} placeholder="https://.../background.mp3" /></label>
          </div>

          <div className="seedance-options-grid">
            <label><span>画幅</span><select value={form.ratio} onChange={(event) => setForm({ ...form, ratio: event.target.value as GenerationForm["ratio"] })}><option value="9:16">9:16 竖屏</option><option value="16:9">16:9 横屏</option><option value="1:1">1:1 方形</option><option value="4:3">4:3 横向</option><option value="3:4">3:4 竖向</option></select></label>
            <label><span>分辨率</span><select value={form.resolution} onChange={(event) => setForm({ ...form, resolution: event.target.value as GenerationForm["resolution"] })}><option value="480p">480p</option><option value="720p">720p</option><option value="1080p">1080p</option></select></label>
            <label><span>时长（4–30 秒）</span><input type="number" min="4" max="30" step="1" value={form.duration} onChange={(event) => setForm({ ...form, duration: Number(event.target.value) })} required /></label>
            <label className="provider-checkbox-field"><span>声音</span><span><input type="checkbox" checked={form.generateAudio} onChange={(event) => setForm({ ...form, generateAudio: event.target.checked })} />同时生成音频</span></label>
          </div>

          <label className="seedance-cost-confirm">
            <input type="checkbox" checked={form.costConfirmed} onChange={(event) => setForm({ ...form, costConfirmed: event.target.checked })} />
            <span><strong>我确认本次提交会产生火山方舟视频生成费用</strong><small>系统不会自动重试提交，避免超时情况下重复扣费。</small></span>
          </label>
        </div>
        <footer>
          <span>生成完成后先保存到 ACTALE，评审通过后再人工应用到草稿。</span>
          <button className="primary-button" disabled={submitting || !config.configured || !form.prompt.trim() || !form.costConfirmed}>{submitting ? "正在提交任务…" : config.configured ? "提交视频生成" : "Seedance 未就绪"}</button>
        </footer>
      </form>

      <section className="uploaded-clip-workbench">
        <header>
          <div><strong>导入同步对话片段</strong><small>导入已在外部完成口型和对白同步的 MP4，供故事序幕合成使用。</small></div>
          <span>不调用模型 · 不修改草稿</span>
        </header>
        <form className="uploaded-clip-form" onSubmit={(event) => void uploadDialogueClip(event)}>
          <label><span>关联故事线</span><select value={uploadStoryId} disabled={uploadingClip} onChange={(event) => { setUploadStoryId(event.target.value); setUploadClientRequestId(crypto.randomUUID()); }} required><option value="">请选择故事线</option>{stories.map((story) => <option key={story.id} value={story.id}>{story.title} · 草稿 R{story.draftRevision}</option>)}</select></label>
          <label><span>片段名称</span><input value={uploadLabel} maxLength={120} disabled={uploadingClip} onChange={(event) => { setUploadLabel(event.target.value); setUploadClientRequestId(crypto.randomUUID()); }} placeholder="例如：第2段 · 米娅说明林叔失联" required /></label>
          <label className="uploaded-clip-file"><span>MP4 文件</span><input key={uploadInputKey} type="file" accept="video/mp4,.mp4" disabled={uploadingClip} onChange={(event) => { setUploadFile(event.currentTarget.files?.[0] ?? null); setUploadClientRequestId(crypto.randomUUID()); }} required /><small>{uploadFile ? `${uploadFile.name} · ${formatMegabytes(uploadFile.size)}` : "建议使用 9:16 MP4，1KB–60MB；服务器会检测编码、尺寸、时长和实际音轨。"}</small></label>
          <button className="primary-button" disabled={uploadingClip || !uploadStoryId || !uploadLabel.trim() || !uploadFile}>{uploadingClip ? "正在上传并检测…" : "导入片段"}</button>
        </form>
        {uploadError ? <div className="error-notice uploaded-clip-feedback" role="alert"><span>!</span>{uploadError}</div> : null}
        {uploadNotice ? <div className="director-notice uploaded-clip-feedback" role="status">✓ {uploadNotice}</div> : null}
        <div className="uploaded-clip-library">
          <header><strong>已导入片段</strong><small>{uploadedClips.length} 条 · 可直接播放评审</small></header>
          {!uploadedClips.length ? <p className="prelude-composer-empty">还没有导入片段。上传不会调用模型，也不会修改故事草稿。</p> : <div className="uploaded-clip-list">{uploadedClips.map((clip) => <article key={clip.id}>
            <video src={storyEntryMediaUrl(clip.managedVideoKey, "mp4")} poster={storyEntryMediaUrl(clip.posterKey, "jpg")} controls playsInline preload="metadata" />
            <div><strong>{clip.label}</strong><span>{clip.story.title}</span><small>{formatClipDuration(clip.managedVideoDurationSeconds)} 秒 · {clip.managedVideoWidth}×{clip.managedVideoHeight} · {formatMegabytes(clip.managedVideoBytes)}</small><em className={`prelude-source-audio ${clip.managedVideoHasAudio ? "voiced" : "silent"}`}>{sourceClipAudioLabel(clip.managedVideoHasAudio)}</em><small>{formatUpdatedAt(clip.createdAt)}</small></div>
          </article>)}</div>}
        </div>
      </section>

      <section className="prelude-composer">
        <header>
          <div><strong>故事序幕制作</strong><small>选择已保存的片段、声音模式和顺序，合成为一个完整的竖屏序幕。</small></div>
          <span>不调用 Seedance</span>
        </header>
        <div className="prelude-composer-body">
          <label className="prelude-composer-story">
            <span>关联故事线</span>
            <select value={compositionStoryId} onChange={(event) => {
              setCompositionStoryId(event.target.value);
              setCompositionSourceKeys([]);
            }}>
              <option value="">请选择故事线</option>
              {stories.map((story) => <option key={story.id} value={story.id}>{story.title} · 草稿 R{story.draftRevision}</option>)}
            </select>
          </label>
          <fieldset className="prelude-audio-mode">
            <legend>合成声音模式</legend>
            {(Object.entries(compositionAudioModeCopy) as Array<[StoryPreludeCompositionAudioMode, typeof compositionAudioModeCopy[StoryPreludeCompositionAudioMode]]>).map(([mode, copy]) => <label className={compositionAudioMode === mode ? "selected" : ""} key={mode}>
              <input type="radio" name="composition-audio-mode" value={mode} checked={compositionAudioMode === mode} onChange={() => setCompositionAudioMode(mode)} />
              <span><strong>{copy.title}</strong><small>{copy.description}</small></span>
            </label>)}
          </fieldset>
          <div className={`prelude-composer-note ${compositionAudioMode === "PRESERVE_SOURCE_AUDIO" ? "warning" : ""}`}>
            <strong>将输出：{compositionAudioModeCopy[compositionAudioMode].outputLabel}</strong>
            <span>{compositionAudioMode === "PRESERVE_SOURCE_AUDIO"
              ? "只在人物对白已经包含在对应原片、并且口型与声音同步时使用。该模式不会修复错位音频，也不会为无声片段生成对白。"
              : "系统会移除所有片段的原始音频，统一输出 9:16、720p 无声视频。"}</span>
          </div>
          <div className="prelude-composer-grid">
            <div>
              <header><strong>可选片段</strong><small>Seedance 候选需为 9:16 / 720p；导入片段会在合成时统一为 9:16 / 720p。</small></header>
              {!compositionCandidates.length ? <p className="prelude-composer-empty">还没有可用于合成的片段。可以保存 Seedance 成片，或在上方导入同步对话片段。</p> : (
                <div className="prelude-candidate-list">
                  {compositionCandidates.map((candidate) => {
                    const selected = compositionSourceKeys.includes(candidate.key);
                    return <label className={`prelude-candidate ${selected ? "selected" : ""}`} key={candidate.key}>
                      <input type="checkbox" checked={selected} onChange={() => toggleCompositionClip(candidate.key)} disabled={!selected && compositionSourceKeys.length >= 6} />
                      <span><strong>{selected ? `第 ${compositionSourceKeys.indexOf(candidate.key) + 1} 段 · ${candidate.label}` : candidate.label}</strong><small>{candidate.sourceLabel} · {formatUpdatedAt(candidate.createdAt)} · {formatClipDuration(candidate.duration)} 秒</small></span>
                      <em className={`prelude-source-audio ${candidate.managedVideoHasAudio === true ? "voiced" : candidate.managedVideoHasAudio === false ? "silent" : "unchecked"}`} title={candidate.managedVideoHasAudio == null ? "仅显示生成时的声音请求；已保存媒体尚未完成实际音轨检测" : "根据已保存媒体的实际音轨检测结果显示"}>{sourceClipAudioLabel(candidate.managedVideoHasAudio, candidate.requestedAudio)}</em>
                    </label>;
                  })}
                </div>
              )}
            </div>
            <div>
              <header><strong>合成顺序</strong><small>{selectedCompositionClips.length} / 6 段 · 预计 {formatClipDuration(selectedCompositionDuration)} 秒 · 有声 {selectedCompositionVoicedCount} / 无声 {selectedCompositionSilentCount} / 待检测 {selectedCompositionUncheckedCount}</small></header>
              {!selectedCompositionClips.length ? <p className="prelude-composer-empty">勾选至少 2 段素材后，在这里调整顺序。</p> : (
                <ol className="prelude-order-list">
                  {selectedCompositionClips.map((candidate, index) => <li key={candidate.key}>
                    <span><strong>第 {index + 1} 段 · {candidate.label}</strong><small>{candidate.sourceLabel} · {formatClipDuration(candidate.duration)} 秒 · {sourceClipAudioLabel(candidate.managedVideoHasAudio, candidate.requestedAudio)}</small></span>
                    <div><button type="button" className="secondary-button" disabled={index === 0} onClick={() => moveCompositionClip(index, -1)}>上移</button><button type="button" className="secondary-button" disabled={index === selectedCompositionClips.length - 1} onClick={() => moveCompositionClip(index, 1)}>下移</button></div>
                  </li>)}
                </ol>
              )}
            </div>
          </div>
        </div>
        <footer>
          <span>将输出“{compositionAudioModeCopy[compositionAudioMode].outputLabel}”；完成后先在后台评审，不会自动应用故事草稿。</span>
          <button type="button" className="primary-button" disabled={composing || selectedCompositionClips.length < 2 || !compositionStoryId} onClick={() => void createComposition()}>{composing ? "正在创建合成任务…" : `合成${compositionAudioModeCopy[compositionAudioMode].outputLabel}`}</button>
        </footer>
      </section>

      <section className="prelude-composition-history">
        <header><div><strong>完整故事序幕</strong><small>保留源片段、合成结果和草稿应用记录。</small></div><span>{compositions.length} 条</span></header>
        {!compositions.length ? <div className="prelude-composer-empty">尚未合成完整故事序幕。</div> : <div className="prelude-composition-list">
          {compositions.map((composition) => {
            const preservesSourceAudio = composition.audioMode === "PRESERVE_SOURCE_AUDIO";
            const preferredOutputLabel = compositionPreferredOutputLabel(composition.audioMode, composition.audioStatus);
            const compositionVoicedCount = composition.sourceClips.filter((clip) => clip.managedVideoHasAudio === true).length;
            const compositionSilentCount = composition.sourceClips.filter((clip) => clip.managedVideoHasAudio === false).length;
            const compositionUncheckedCount = composition.sourceClips.filter((clip) => clip.managedVideoHasAudio == null).length;
            const baseOutputUrl = composition.outputVideoKey
              ? storyEntryMediaUrl(composition.outputVideoKey, "mp4")
              : null;
            const voicedOutputUrl = composition.voicedOutputVideoKey
              ? storyEntryMediaUrl(composition.voicedOutputVideoKey, "mp4")
              : null;
            const audioBusy = composition.audioStatus === "QUEUED" || composition.audioStatus === "RUNNING";
            return <article className={`prelude-composition ${composition.status.toLowerCase()}`} key={composition.id}>
              <header><span className="seedance-job-status">{compositionStatusText[composition.status]}</span><div><strong>{composition.story.title}</strong><small>{compositionAudioModeCopy[composition.audioMode].outputLabel} · {composition.sourceClips.length} 段 · 预计 {composition.sourceClips.reduce((total, clip) => total + clip.duration, 0)} 秒 · {formatUpdatedAt(composition.createdAt)}</small></div></header>
              {baseOutputUrl && composition.outputVideoKey ? <div className="prelude-composition-result">
                <div className="prelude-output-grid">
                  <div className="prelude-output-card">
                    <video src={baseOutputUrl} controls playsInline preload="metadata" poster={storyEntryMediaUrl(composition.outputVideoKey, "jpg")} />
                    <span>{compositionAudioModeCopy[composition.audioMode].outputLabel} · {composition.outputDurationSeconds ?? "—"} 秒</span>
                  </div>
                  {voicedOutputUrl && composition.voicedOutputVideoKey ? <div className="prelude-output-card featured">
                    <video src={voicedOutputUrl} controls playsInline preload="metadata" poster={storyEntryMediaUrl(composition.voicedOutputVideoKey, "jpg")} />
                    <span>{preferredOutputLabel} · {composition.voicedOutputDurationSeconds ?? "—"} 秒</span>
                  </div> : null}
                </div>
                <div className="prelude-audio-actions">
                  <strong>{compositionAudioStatusLabel(composition.audioMode, composition.audioStatus)}</strong>
                  <small>{compositionAudioModeCopy[composition.audioMode].outputLabel} {((composition.outputVideoBytes ?? 0) / 1024 / 1024).toFixed(1)}MB · 源片段顺序 {composition.sourceClips.map((clip) => clip.sortOrder).join(" → ")}</small>
                  <div className="prelude-composition-source-list">{[...composition.sourceClips].sort((left, right) => left.sortOrder - right.sortOrder).map((clip) => <div key={`${clip.kind}:${clip.sourceId}`}>
                    <strong>第 {clip.sortOrder} 段 · {clip.kind === "UPLOADED_CLIP" ? clip.label ?? "导入片段" : "Seedance 生成片段"}</strong>
                    <span>{clip.kind === "UPLOADED_CLIP" ? "导入片段" : "Seedance"} · {formatClipDuration(clip.duration)} 秒 · {sourceClipAudioLabel(clip.managedVideoHasAudio)}</span>
                  </div>)}</div>
                  <small>实际音轨：有声 {compositionVoicedCount} / 无声 {compositionSilentCount} / 待检测 {compositionUncheckedCount}</small>
                  {composition.masterAudioAt ? <small>{preservesSourceAudio ? "环境/音乐底轨" : "完整替换母音轨"} {composition.masterAudioMimeType ?? "—"} · {(composition.masterAudioDurationSeconds ?? 0).toFixed(1)} 秒 · {((composition.masterAudioBytes ?? 0) / 1024 / 1024).toFixed(1)}MB</small> : null}
                  <div className={`prelude-master-audio-note ${preservesSourceAudio ? "mix" : "replace"}`}>
                    <strong>{preservesSourceAudio ? "环境/音乐底轨：与原片对白混合" : "完整母音轨：替换所有源片段声音"}</strong>
                    <span>{preservesSourceAudio ? "请先在外部预调底轨音量，且不要在底轨中重复加入人物对白。" : "适用于无声画面版；上传后不会保留任何源片段声音。"}</span>
                  </div>
                  <label className={`prelude-audio-upload ${audioBusy || uploadingAudioId === composition.id ? "disabled" : ""}`}>
                    <input
                      type="file"
                      accept="audio/mp4,audio/x-m4a,audio/mpeg,audio/wav,audio/webm,audio/aac,.m4a,.mp3,.wav,.webm,.aac"
                      disabled={audioBusy || uploadingAudioId === composition.id}
                      onChange={(event) => {
                        const file = event.currentTarget.files?.[0];
                        event.currentTarget.value = "";
                        if (file) void uploadMasterAudio(composition, file);
                      }}
                    />
                    <span>{uploadingAudioId === composition.id ? "正在上传…" : composition.audioStatus === "SUCCEEDED" ? `替换${preservesSourceAudio ? "环境/音乐底轨" : "完整母音轨"}` : `上传${preservesSourceAudio ? "环境/音乐底轨" : "完整母音轨"}`}</span>
                  </label>
                  <small>支持 M4A、MP3、WAV、WebM、AAC，最大 40MB；与画面时长相差不能超过 2 秒。</small>
                  {composition.appliedToCurrentStoryDraft ? <span>当前“{preferredOutputLabel}”已应用到草稿 R{composition.appliedStoryRevision ?? composition.story.draftRevision}</span> : <button
                    type="button"
                    className="secondary-button"
                    disabled={audioBusy || applyingCompositionId === composition.id || !composition.preferredOutputVideoKey}
                    onClick={() => void applyComposition(composition)}
                  >{applyingCompositionId === composition.id ? "正在应用…" : `应用${preferredOutputLabel}到草稿`}</button>}
                  <em>合成不会修改故事；只有点击应用才回填草稿，且不会发布 StoryVersion。</em>
                </div>
              </div> : null}
              {composition.status === "FAILED" ? <div className="seedance-job-error"><strong>{composition.errorCode ?? "STORY_PRELUDE_COMPOSITION_FAILED"}</strong><span>请检查源片段是否仍在 ACTALE 媒体库中，再决定是否重新合成。</span></div> : null}
              {composition.audioStatus === "FAILED" ? <div className="seedance-job-error"><strong>{composition.audioErrorCode ?? "STORY_PRELUDE_AUDIO_MIX_FAILED"}</strong><span>基础合成片仍然保留。请检查母音轨格式、时长和混音模式后重新上传。</span></div> : null}
            </article>;
          })}
        </div>}
      </section>

      <section className="seedance-history">
        <header><div><strong>最近生成任务</strong><small>自动查询排队和生成中的任务</small></div><span>{jobs.length} 条</span></header>
        {!jobs.length ? <div className="empty-state"><span className="empty-glyph" aria-hidden="true">影</span><h2>还没有视频任务</h2><p>Seedance 配置完成后，在上方提交第一条生成任务。</p></div> : null}
        <div className="seedance-job-list">
          {jobs.map((job) => {
            const story = stories.find((item) => item.id === job.story?.id) ?? null;
            const managedVideoUrl = job.managedVideoKey
              ? storyEntryMediaUrl(job.managedVideoKey, "mp4")
              : null;
            return (
            <article className={`seedance-job ${job.status.toLowerCase()}`} key={job.id}>
              <div className="seedance-job-summary">
                <span className="seedance-job-status">{statusText[job.status]}</span>
                <div><strong>{job.story?.title ?? "未关联故事线"}</strong><small>{formatUpdatedAt(job.createdAt)} · {job.duration}s · {job.ratio} · {job.resolution} · {sourceClipAudioLabel(job.managedVideoHasAudio, job.generateAudio)}</small></div>
                <button className="secondary-button" type="button" disabled={refreshingId === job.id || terminalStatuses.has(job.status)} onClick={() => void refreshJob(job.id)}>{refreshingId === job.id ? "查询中…" : terminalStatuses.has(job.status) ? "已结束" : "刷新进度"}</button>
              </div>
              {job.status === "SUCCEEDED" && job.outputVideoUrl ? (
                <div className="seedance-result">
                  {managedVideoUrl && job.managedVideoKey ? (
                    <video
                      src={managedVideoUrl}
                      controls
                      playsInline
                      preload="metadata"
                      poster={storyEntryMediaUrl(job.managedVideoKey, "jpg")}
                    />
                  ) : (
                    <div className="seedance-result-pending">
                      <strong>供应商成片尚未保存</strong>
                      <span>原始地址可能过期或加载缓慢。先保存到 ACTALE，系统会生成稳定地址和首帧。</span>
                    </div>
                  )}
                  <div className="seedance-result-actions">
                    {!managedVideoUrl ? (
                      <button className="primary-button compact" type="button" disabled={importingId === job.id} onClick={() => void importJob(job.id)}>
                        {importingId === job.id ? "正在保存并优化，可能需要数分钟…" : "保存到 ACTALE 并准备播放"}
                      </button>
                    ) : (
                      <a className="primary-button compact" href={managedVideoUrl} target="_blank" rel="noreferrer">打开 ACTALE 成片</a>
                    )}
                    {managedVideoUrl && story ? (
                      job.appliedToCurrentStoryDraft
                        ? <span>历史视频已应用到草稿 R{job.appliedStoryRevision ?? story.draftRevision}</span>
                        : <small>单段素材不能直接应用到故事草稿；请在“故事序幕制作”中合成完整序幕。</small>
                    ) : null}
                    <a className="seedance-source-link" href={job.outputVideoUrl} target="_blank" rel="noreferrer">供应商原始文件</a>
                    {job.managedVideoKey ? <span>素材：{job.managedVideoKey} · {((job.managedVideoBytes ?? 0) / 1024 / 1024).toFixed(1)}MB</span> : null}
                    <span>方舟任务：{job.providerTaskId ?? "—"}</span>
                    <small>保存成片不会修改故事。完整序幕应用到草稿后，StoryVersion 仍不会自动发布。</small>
                  </div>
                </div>
              ) : null}
              {job.status === "FAILED" || job.status === "EXPIRED" || job.status === "CANCELLED" ? <div className="seedance-job-error"><strong>{job.errorCode ?? statusText[job.status]}</strong><span>检查 Key、模型权限、素材合规和账号额度后，再决定是否重新提交。</span></div> : null}
              <details><summary>查看提示词和素材</summary><p>{job.prompt}</p><dl><div><dt>图片</dt><dd>{job.referenceImageUrls.length}</dd></div><div><dt>视频</dt><dd>{job.referenceVideoUrls.length}</dd></div><div><dt>音频</dt><dd>{job.referenceAudioUrls.length}</dd></div><div><dt>Token</dt><dd>{job.totalTokens?.toLocaleString() ?? "—"}</dd></div></dl></details>
            </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
